/**
 * School Age Sign-In Card Component
 * Special card with AM Signed In, PM Drop Off, and PM Parent Pickup tracking
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Clock, Trash2, Edit2, Hand, UserPlus, ShieldCheck, User, Sun, LogOut } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import VoiceInput from "@/components/VoiceInput";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { usePhotoRenewal } from "@/hooks/usePhotoRenewal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Safely format a date string; returns null instead of throwing if missing/invalid
const formatDateSafe = (dateStr, pattern) => {
  if (!dateStr) return null;
  try {
    const parsed = parseISO(dateStr);
    if (!isValid(parsed)) return null;
    return format(parsed, pattern);
  } catch {
    return null;
  }
};


// Photo avatar with error handling + renewal (safe for an unattended, long-open sign-in tablet)
const ChildPhotoAvatar = ({ child, childName, colorClass = "green" }) => {
  const { src, failed, handleError } = usePhotoRenewal(child?.id, child?.photoUrl);
  const hasValidPhoto = !!src && !failed;
  
  if (hasValidPhoto) {
    return (
      <img
        src={src}
        alt={childName}
        className={`w-16 h-16 rounded-full object-cover border-2 border-${colorClass}-200 flex-shrink-0`}
        onError={handleError}
        onLoad={(e) => { if (e.target.naturalWidth <= 2 || e.target.naturalHeight <= 2) handleError(); }}
      />
    );
  }
  
  return (
    <div className={`w-16 h-16 rounded-full bg-${colorClass}-50 border-2 border-${colorClass}-200 flex items-center justify-center flex-shrink-0`}>
      <User className={`w-7 h-7 text-${colorClass}-300`} />
    </div>
  );
};

export const SchoolAgeSignInCard = ({
  signIn,
  child,
  pickupPersons,
  index = 0,
  onAmDropOff,
  onClearAmDropOff,
  onPmBusDropoff,
  onClearPmBus,
  onPmParentPickup,
  onClearPmParent,
  onManualTimeUpdate,
  onSaveTimeToServer,
  onNotesChange,
  onReactivate,
  onManualPersonEntry,
  onStayAllDayToggle,
  onMidDayOut,
  onMidDayReturn,
  onClearMidDay,
}) => {
  const [showMidDayForm, setShowMidDayForm] = useState(false);
  const [midDayReason, setMidDayReason] = useState('');
  const [midDayCustomReason, setMidDayCustomReason] = useState('');
  // Manual entry states for AM Drop Off
  const [showManualAmDropOff, setShowManualAmDropOff] = useState(false);
  const [manualAmDropOffName, setManualAmDropOffName] = useState("");
  const [manualAmDropOffApprover, setManualAmDropOffApprover] = useState("");
  
  // Manual entry states for PM Pickup
  const [showManualPmPickup, setShowManualPmPickup] = useState(false);
  const [manualPmPickupName, setManualPmPickupName] = useState("");
  const [manualPmPickupApprover, setManualPmPickupApprover] = useState("");
  
  // Edit mode states for time fields
  const [editingAmTime, setEditingAmTime] = useState(false);
  const [editingPmDropOffTime, setEditingPmDropOffTime] = useState(false);
  const [editingPmPickupTime, setEditingPmPickupTime] = useState(false);

  const isDeactivated = child && child.active === false;
  
  // Feature access check for voice input
  const { hasAccess: hasVoiceAccess } = useFeatureAccess('voice_input');

  // Format the notes updated info
  const getNotesUpdatedInfo = () => {
    if (signIn.notes && signIn.notes.trim()) {
      // Check if we have staff notes info
      if (signIn.staffNotesUpdatedBy && signIn.staffNotesUpdatedAt) {
        try {
          return `by ${signIn.staffNotesUpdatedBy.split('@')[0]} at ${format(parseISO(signIn.staffNotesUpdatedAt), 'h:mm a')}`;
        } catch {
          return `by ${signIn.staffNotesUpdatedBy.split('@')[0]}`;
        }
      }
      // Check if we have owner notes info
      if (signIn.lastModifiedBy && signIn.lastModifiedAt) {
        try {
          return `by ${signIn.lastModifiedBy.split('@')[0]} at ${format(parseISO(signIn.lastModifiedAt), 'h:mm a')}`;
        } catch {
          return `by ${signIn.lastModifiedBy.split('@')[0]}`;
        }
      }
    }
    return null;
  };

  // Calculate age from birthdate
  const getAge = () => {
    if (!child?.birthdate) return null;
    try {
      const birthDate = parseISO(child.birthdate);
      const today = new Date();
      let years = today.getFullYear() - birthDate.getFullYear();
      let months = today.getMonth() - birthDate.getMonth();
      
      if (months < 0) {
        years--;
        months += 12;
      }
      if (today.getDate() < birthDate.getDate()) {
        months--;
        if (months < 0) {
          years--;
          months += 12;
        }
      }
      
      if (years > 0) {
        return `${years}y ${months}m`;
      }
      return `${months}m`;
    } catch {
      return null;
    }
  };

  const childAge = getAge();
  
  // Alternating card styles - green color scheme for School Age
  const isEvenCard = index % 2 === 0;
  const cardBackground = isEvenCard 
    ? 'linear-gradient(135deg, rgba(220,252,231,0.9) 0%, rgba(187,247,208,0.85) 50%, rgba(209,250,229,0.9) 100%)'
    : 'rgba(255,255,255,0.95)';
  const cardBorder = '2px solid rgba(34,197,94,0.4)';

  return (
    <Card 
      className={`border-0 transition-all duration-200 print:shadow-none print:border print:border-gray-300 ${
        !child ? 'border border-red-300/50' : 
        isDeactivated ? 'opacity-75' : ''
      }`}
      style={{
        background: cardBackground,
        borderRadius: '20px',
        border: !child ? '2px solid rgba(239,68,68,0.5)' : isDeactivated ? '2px solid rgba(156,163,175,0.4)' : cardBorder
      }}
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Child Photo */}
            <ChildPhotoAvatar child={child} childName={signIn.childName} colorClass="green" />
            <span className={`text-xl ${isDeactivated ? 'text-gray-500' : 'text-blue-900'}`}>
              {signIn.childName}
            </span>
            {childAge && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                {childAge}
              </span>
            )}
            {formatDateSafe(child?.birthdate, 'M/d/yyyy') && (
              <span className="text-xs text-gray-500">
                DOB: {formatDateSafe(child?.birthdate, 'M/d/yyyy')}
              </span>
            )}
            {child && child.school && !isDeactivated && (
              <span className="text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                • {child.school}
              </span>
            )}
            {!child && (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                Child record not found
              </span>
            )}
            {isDeactivated && (
              <div className="flex items-center gap-2">
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                  Deactivated {formatDateSafe(child.deactivatedAt, 'MMM d, yyyy') || ''}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-xs border-green-500 text-green-600 hover:bg-green-50"
                  onClick={() => onReactivate(child)}
                >
                  Reactivate
                </Button>
              </div>
            )}
          </div>
          {/* Status indicators */}
          <div className="flex items-center gap-2 text-xs">
            {signIn.amParentDropOffTime && (
              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">AM ✓</span>
            )}
            {signIn.pmSchoolAgeDropOffTime && (
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded">PM In ✓</span>
            )}
            {signIn.pmParentPickupTime && (
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">PM Out ✓</span>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* AM Signed In */}
          <div className="space-y-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between">
              <Label className="text-purple-800 font-semibold flex items-center gap-2">
                <span className="text-lg">🚗</span> AM Signed In
              </Label>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-purple-600 hover:bg-purple-100 text-xs"
                onClick={() => setShowManualAmDropOff(!showManualAmDropOff)}
                title="Add person not on list"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                {showManualAmDropOff ? 'Use List' : 'Other'}
              </Button>
            </div>
            
            {showManualAmDropOff ? (
              /* Manual AM Drop-Off Entry */
              <div className="space-y-2 p-3 bg-purple-100 border border-purple-300 rounded-lg">
                <p className="text-xs text-purple-700 flex items-center gap-1 font-medium">
                  <ShieldCheck className="w-3 h-3" />
                  Person below has written or verbal approval from parent
                </p>
                <Input
                  value={manualAmDropOffName}
                  onChange={(e) => setManualAmDropOffName(e.target.value)}
                  placeholder="Enter name of drop-off person"
                  className="border-purple-200 bg-white text-sm"
                />
                <Input
                  value={manualAmDropOffApprover}
                  onChange={(e) => setManualAmDropOffApprover(e.target.value)}
                  placeholder="Approved by (parent/guardian name)"
                  className="border-purple-200 bg-white text-sm"
                />
                <Button
                  size="sm"
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                  disabled={!manualAmDropOffName.trim()}
                  onClick={() => {
                    const approvalNote = manualAmDropOffApprover.trim() 
                      ? ` [Approved by: ${manualAmDropOffApprover.trim()}]` 
                      : ' [Manual entry - approval on file]';
                    if (onManualPersonEntry) {
                      onManualPersonEntry(signIn, 'amDropOff', manualAmDropOffName.trim() + approvalNote);
                    } else {
                      onAmDropOff(signIn, manualAmDropOffName.trim() + approvalNote);
                    }
                    setShowManualAmDropOff(false);
                    setManualAmDropOffName("");
                    setManualAmDropOffApprover("");
                  }}
                >
                  Record AM Drop Off
                </Button>
              </div>
            ) : signIn.amParentDropOffTime ? (
              <div className="space-y-2">
                {signIn.amDropOffPerson && (
                  <Select
                    value={signIn.amDropOffPerson || ""}
                    onValueChange={(value) => {
                      if (value === "__clear__") {
                        onClearAmDropOff(signIn);
                      } else {
                        onAmDropOff(signIn, value);
                      }
                    }}
                  >
                    <SelectTrigger className="border-purple-200 bg-white">
                      <SelectValue placeholder="Select who dropped off" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__clear__" className="text-red-600">
                        ✕ Clear Selection
                      </SelectItem>
                      {pickupPersons.map((person, idx) => (
                        <SelectItem key={idx} value={person.name}>
                          {person.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-100 p-2 rounded">
                  <Clock className="w-4 h-4" />
                  {editingAmTime ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="text"
                        value={signIn.amParentDropOffTime || ""}
                        onChange={(e) => onManualTimeUpdate(signIn, 'amParentDropOffTime', e.target.value)}
                        onBlur={(e) => {
                          onSaveTimeToServer(signIn, 'amParentDropOffTime', e.target.value);
                          setEditingAmTime(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onSaveTimeToServer(signIn, 'amParentDropOffTime', e.target.value);
                            setEditingAmTime(false);
                          }
                        }}
                        placeholder="7:00 AM"
                        className="h-7 border-purple-200 bg-white text-sm w-24"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-purple-600 hover:bg-purple-200"
                        onClick={() => setEditingAmTime(false)}
                      >
                        ✓
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-1">
                      <span>Dropped off at <strong>{signIn.amParentDropOffTime}</strong></span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0 text-purple-600 hover:bg-purple-200"
                        onClick={() => setEditingAmTime(true)}
                        title="Edit time"
                        data-testid={`edit-am-dropoff-${signIn.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0 text-red-500 hover:bg-red-100"
                        onClick={() => onClearAmDropOff && onClearAmDropOff(signIn)}
                        title="Delete AM drop-off time"
                        data-testid={`delete-am-dropoff-${signIn.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                {/* Show approval note if AM drop-off person has approval info */}
                {signIn.amDropOffPerson && signIn.amDropOffPerson.includes('[Approved by:') && (
                  <p className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    {signIn.amDropOffPerson}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Select
                  onValueChange={(value) => {
                    onAmDropOff(signIn, value);
                  }}
                >
                  <SelectTrigger className="border-purple-200 bg-white">
                    <SelectValue placeholder="Select who dropped off" />
                  </SelectTrigger>
                  <SelectContent>
                    {pickupPersons.map((person, idx) => (
                      <SelectItem key={idx} value={person.name}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* PM Drop Off */}
          <div className="space-y-2 p-3 bg-green-50 rounded-lg border border-green-200">
            <Label className="text-green-800 font-semibold flex items-center gap-2">
              <span className="text-lg">🚌</span> PM Drop Off
            </Label>
            {signIn.pmSchoolAgeDropOffTime ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-100 p-2 rounded">
                  <Clock className="w-4 h-4" />
                  {editingPmDropOffTime ? (
                    <div className="flex items-center gap-2 flex-1 flex-wrap">
                      <Input
                        type="text"
                        value={signIn.pmSchoolAgeDropOffTime || ""}
                        onChange={(e) => onManualTimeUpdate(signIn, 'pmSchoolAgeDropOffTime', e.target.value)}
                        onBlur={(e) => onSaveTimeToServer(signIn, 'pmSchoolAgeDropOffTime', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onSaveTimeToServer(signIn, 'pmSchoolAgeDropOffTime', e.target.value);
                            setEditingPmDropOffTime(false);
                          }
                        }}
                        placeholder="3:30 PM"
                        className="h-7 border-green-200 bg-white text-sm w-24"
                        data-testid={`edit-pm-dropoff-time-input-${signIn.id}`}
                        autoFocus
                      />
                      <span className="text-xs text-green-700">by</span>
                      <Input
                        type="text"
                        value={signIn.pmSchoolAgeDropOffPerson || ""}
                        onChange={(e) => onManualTimeUpdate(signIn, 'pmSchoolAgeDropOffPerson', e.target.value)}
                        onBlur={(e) => onSaveTimeToServer(signIn, 'pmSchoolAgeDropOffPerson', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onSaveTimeToServer(signIn, 'pmSchoolAgeDropOffPerson', e.target.value);
                            setEditingPmDropOffTime(false);
                          }
                        }}
                        placeholder="Staff name"
                        className="h-7 border-green-200 bg-white text-sm w-28"
                        data-testid={`edit-pm-dropoff-person-input-${signIn.id}`}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-green-600 hover:bg-green-200"
                        onClick={() => setEditingPmDropOffTime(false)}
                      >
                        ✓
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-1">
                      <div>
                        <span>Dropped off at <strong>{signIn.pmSchoolAgeDropOffTime}</strong></span>
                        {signIn.pmSchoolAgeDropOffPerson && (
                          <p className="text-xs text-green-600">by {signIn.pmSchoolAgeDropOffPerson}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0 text-green-600 hover:bg-green-200"
                        onClick={() => setEditingPmDropOffTime(true)}
                        title="Edit time/name"
                        data-testid={`edit-pm-dropoff-${signIn.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0 text-red-500 hover:bg-red-100"
                        onClick={() => onClearPmBus && onClearPmBus(signIn)}
                        title="Delete PM drop-off time"
                        data-testid={`delete-pm-dropoff-${signIn.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                onClick={() => onPmBusDropoff(signIn)}
              >
                PM Drop Off
              </Button>
            )}
          </div>

          {/* PM Parent Pickup */}
          <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <Label className="text-blue-800 font-semibold flex items-center gap-2">
                <span className="text-lg">🏠</span> PM Parent Pickup
              </Label>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-blue-600 hover:bg-blue-100 text-xs"
                onClick={() => setShowManualPmPickup(!showManualPmPickup)}
                title="Add person not on list"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                {showManualPmPickup ? 'Use List' : 'Other'}
              </Button>
            </div>
            
            {showManualPmPickup ? (
              /* Manual PM Pickup Entry */
              <div className="space-y-2 p-3 bg-blue-100 border border-blue-300 rounded-lg">
                <p className="text-xs text-blue-700 flex items-center gap-1 font-medium">
                  <ShieldCheck className="w-3 h-3" />
                  Person below has written or verbal approval from parent
                </p>
                <Input
                  value={manualPmPickupName}
                  onChange={(e) => setManualPmPickupName(e.target.value)}
                  placeholder="Enter name of pick-up person"
                  className="border-blue-200 bg-white text-sm"
                />
                <Input
                  value={manualPmPickupApprover}
                  onChange={(e) => setManualPmPickupApprover(e.target.value)}
                  placeholder="Approved by (parent/guardian name)"
                  className="border-blue-200 bg-white text-sm"
                />
                <Button
                  size="sm"
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  disabled={!manualPmPickupName.trim()}
                  onClick={() => {
                    const approvalNote = manualPmPickupApprover.trim() 
                      ? ` [Approved by: ${manualPmPickupApprover.trim()}]` 
                      : ' [Manual entry - approval on file]';
                    if (onManualPersonEntry) {
                      onManualPersonEntry(signIn, 'pmPickUp', manualPmPickupName.trim() + approvalNote);
                    } else {
                      onPmParentPickup(signIn, manualPmPickupName.trim() + approvalNote);
                    }
                    setShowManualPmPickup(false);
                    setManualPmPickupName("");
                    setManualPmPickupApprover("");
                  }}
                >
                  Record PM Pick Up
                </Button>
              </div>
            ) : signIn.pmParentPickupTime ? (
              <div className="space-y-2">
                {signIn.pmParentPickupPerson && (
                  <Select
                    value={signIn.pmParentPickupPerson || ""}
                    onValueChange={(value) => {
                      if (value === "__clear__") {
                        onClearPmParent(signIn);
                      } else {
                        onPmParentPickup(signIn, value);
                      }
                    }}
                  >
                    <SelectTrigger className="border-blue-200 bg-white">
                      <SelectValue placeholder="Select who picked up" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__clear__" className="text-red-600">
                        ✕ Clear Selection
                      </SelectItem>
                      {pickupPersons.map((person, idx) => (
                        <SelectItem key={idx} value={person.name}>
                          {person.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-100 p-2 rounded">
                  <Clock className="w-4 h-4" />
                  {editingPmPickupTime ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="text"
                        value={signIn.pmParentPickupTime || ""}
                        onChange={(e) => onManualTimeUpdate(signIn, 'pmParentPickupTime', e.target.value)}
                        onBlur={(e) => {
                          onSaveTimeToServer(signIn, 'pmParentPickupTime', e.target.value);
                          setEditingPmPickupTime(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onSaveTimeToServer(signIn, 'pmParentPickupTime', e.target.value);
                            setEditingPmPickupTime(false);
                          }
                        }}
                        placeholder="5:30 PM"
                        className="h-7 border-blue-200 bg-white text-sm w-24"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-blue-600 hover:bg-blue-200"
                        onClick={() => setEditingPmPickupTime(false)}
                      >
                        ✓
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-1">
                      <span>Picked up at <strong>{signIn.pmParentPickupTime}</strong></span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0 text-blue-600 hover:bg-blue-200"
                        onClick={() => setEditingPmPickupTime(true)}
                        title="Edit time"
                        data-testid={`edit-pm-pickup-${signIn.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0 text-red-500 hover:bg-red-100"
                        onClick={() => onClearPmParent && onClearPmParent(signIn)}
                        title="Delete PM pickup time"
                        data-testid={`delete-pm-pickup-${signIn.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                {/* Show approval note if PM pickup person has approval info */}
                {signIn.pmParentPickupPerson && signIn.pmParentPickupPerson.includes('[Approved by:') && (
                  <p className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    {signIn.pmParentPickupPerson}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Select
                  onValueChange={(value) => {
                    onPmParentPickup(signIn, value);
                  }}
                >
                  <SelectTrigger className="border-blue-200 bg-white">
                    <SelectValue placeholder="Select who picked up" />
                  </SelectTrigger>
                  <SelectContent>
                    {pickupPersons.map((person, idx) => (
                      <SelectItem key={idx} value={person.name}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <Label className="text-blue-800 font-semibold">Notes</Label>
            {signIn.notes && signIn.notes.trim() && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNotesChange(signIn, "")}
                className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                title="Clear notes"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="relative">
            <Textarea
              value={signIn.notes || ""}
              onChange={(e) => onNotesChange(signIn, e.target.value)}
              placeholder="Add notes..."
              className="border-blue-200 pr-12"
              data-testid={`notes-${signIn.id}`}
            />
            <div className="absolute right-2 top-2 print:hidden">
              {hasVoiceAccess && (
                <VoiceInput
                  onTranscript={(text) => onNotesChange(signIn, (signIn.notes || "") + " " + text)}
                />
              )}
            </div>
          </div>
          {getNotesUpdatedInfo() && (
            <p className="text-xs text-blue-600 mt-1 text-right">
              {getNotesUpdatedInfo()}
            </p>
          )}
        </div>

        {/* Staying All Day Toggle */}
        <div className={`mt-4 flex items-center justify-between p-3 rounded-xl border-2 transition-all print:border print:border-gray-300 ${
          signIn.stayAllDay 
            ? 'bg-emerald-50 border-emerald-400' 
            : 'bg-gray-50 border-dashed border-gray-300'
        }`}>
          <div className="flex items-center gap-2">
            <Sun className={`w-4 h-4 ${signIn.stayAllDay ? 'text-emerald-600' : 'text-gray-400'}`} />
            <span className={`text-sm font-semibold ${signIn.stayAllDay ? 'text-emerald-800' : 'text-gray-500'}`}>
              Staying All Day
            </span>
            {signIn.stayAllDay && (
              <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full font-bold">
                IN RATIO — FULL DAY
              </span>
            )}
            <span className="hidden print:inline text-xs text-gray-600">
              {signIn.stayAllDay ? '✓ Staying All Day (Full Ratio)' : ''}
            </span>
          </div>
          <button
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none print:hidden ${
              signIn.stayAllDay ? 'bg-emerald-500' : 'bg-gray-300'
            }`}
            onClick={() => onStayAllDayToggle && onStayAllDayToggle(signIn, !signIn.stayAllDay)}
            data-testid={`stay-all-day-toggle-${signIn.id}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
              signIn.stayAllDay ? 'translate-x-6' : 'translate-x-0'
            }`} />
          </button>
        </div>

        {/* Mid-Day Departure */}
        {!signIn.midDayOut?.outTime && !showMidDayForm && (
          <button
            className="mt-3 w-full text-left text-xs text-gray-400 border border-dashed border-gray-300 rounded-xl px-3 py-2 hover:border-amber-400 hover:text-amber-600 transition-colors print:hidden"
            onClick={() => setShowMidDayForm(true)}
            data-testid={`mid-day-out-btn-${signIn.id}`}
          >
            + Mid-Day Departure
          </button>
        )}

        {!signIn.midDayOut?.outTime && showMidDayForm && (
          <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl space-y-2 print:hidden">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
              <LogOut className="w-3 h-3" /> Record Mid-Day Departure
            </p>
            <select
              value={midDayReason}
              onChange={e => setMidDayReason(e.target.value)}
              className="w-full border border-amber-200 rounded-lg px-2 py-1.5 text-sm bg-white"
            >
              <option value="">Select reason...</option>
              <option value="Doctor/Dentist Appointment">Doctor/Dentist Appointment</option>
              <option value="Summer Program">Summer Program</option>
              <option value="Field Trip">Field Trip</option>
              <option value="Family Emergency">Family Emergency</option>
              <option value="Other">Other</option>
            </select>
            {midDayReason === 'Other' && (
              <input
                value={midDayCustomReason}
                onChange={e => setMidDayCustomReason(e.target.value)}
                placeholder="Describe reason..."
                className="w-full border border-amber-200 rounded-lg px-2 py-1.5 text-sm bg-white"
              />
            )}
            <div className="flex gap-2">
              <button
                disabled={!midDayReason || (midDayReason === 'Other' && !midDayCustomReason.trim())}
                className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm rounded-lg py-1.5 font-medium"
                onClick={() => {
                  onMidDayOut && onMidDayOut(signIn, midDayReason, midDayCustomReason);
                  setShowMidDayForm(false);
                  setMidDayReason('');
                  setMidDayCustomReason('');
                }}
              >
                Record Departure
              </button>
              <button
                className="px-3 text-gray-500 hover:text-gray-700 text-sm"
                onClick={() => { setShowMidDayForm(false); setMidDayReason(''); setMidDayCustomReason(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {signIn.midDayOut?.outTime && !signIn.midDayOut?.returnTime && (
          <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-400 rounded-xl print:border print:border-gray-400">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs font-bold text-amber-800 flex items-center gap-1">
                  <LogOut className="w-3 h-3" /> MID-DAY DEPARTURE
                  <span className="ml-1 bg-amber-500 text-white px-2 py-0.5 rounded-full">OUT SINCE {signIn.midDayOut.outTime}</span>
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {signIn.midDayOut.reason === 'Other' ? signIn.midDayOut.customReason : signIn.midDayOut.reason}
                </p>
              </div>
              <button
                className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg font-medium print:hidden"
                onClick={() => onMidDayReturn && onMidDayReturn(signIn)}
                data-testid={`mid-day-return-btn-${signIn.id}`}
              >
                Mark Returned
              </button>
            </div>
          </div>
        )}

        {signIn.midDayOut?.outTime && signIn.midDayOut?.returnTime && (
          <div className="mt-3 p-3 bg-emerald-50 border-2 border-emerald-300 rounded-xl print:border print:border-gray-400">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs font-bold text-emerald-800 flex items-center gap-1">
                  MID-DAY DEPARTURE
                  <span className="ml-1 bg-emerald-600 text-white px-2 py-0.5 rounded-full">RETURNED {signIn.midDayOut.returnTime}</span>
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Out: {signIn.midDayOut.outTime} → Back: {signIn.midDayOut.returnTime} &nbsp;|&nbsp;
                  {signIn.midDayOut.reason === 'Other' ? signIn.midDayOut.customReason : signIn.midDayOut.reason}
                </p>
              </div>
              <button
                className="text-xs text-red-500 hover:text-red-700 print:hidden"
                onClick={() => onClearMidDay && onClearMidDay(signIn)}
                data-testid={`mid-day-clear-btn-${signIn.id}`}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Special Instructions */}
        {child && child.pickupPersons && child.pickupPersons[0]?.restrictions && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Label className="text-yellow-800 font-semibold text-sm">Special Instructions</Label>
            <p className="text-yellow-700 text-sm mt-1">{child.pickupPersons[0].restrictions}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SchoolAgeSignInCard;
